import { Box, Button, Checkbox, Chip, FormControl, FormControlLabel, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, Stack, Typography } from "@mui/material";
import { fieldSx, selectMenuProps } from "../../../pages/Team/TeamPage";
import { useEffect, useState } from "react";
import { updateRole } from "../../../core/services/admin.service";

// Job title -> multi select
// module -> multi select
// permissions -> checkboxes (view, edit only)
// submit button.
function RoleForm({ roleForm, options, updateRoleForm, handleCreateRole, handleUpdateRole }) {
  const [selectedJobTitleIds, setSelectedJobTitleIds] = useState([]);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);

  const jobTitleOptions = options.jobTitles || [];
  const moduleOptions = options.modules || [];

  useEffect(() => {
    if (roleForm.isUpdate) {
      setSelectedJobTitleIds(
        roleForm.jobTitles
      );
      setSelectedModuleIds(
        roleForm.modules
      );
    }
  }, [roleForm]);

  const handleViewChange = (e) => {
    const checked = e.target.checked;
    updateRoleForm('canView', checked);
    // Turning view off should turn edit off too, since edit implies view.
    if (!checked) {
      updateRoleForm('canEdit', false);
    }
  };

  const handleEditChange = (e) => {
    const checked = e.target.checked;
    updateRoleForm('canEdit', checked);
    if (checked) {
      updateRoleForm('canView', true);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const jobTitles = jobTitleOptions.filter((jt) =>
      selectedJobTitleIds.includes(jt.jobTitleId)
    );
    const modules = moduleOptions.filter((m) =>
      selectedModuleIds.includes(m.moduleOperationId)
    );
    if (roleForm.isUpdate) {
      const roleUpdatePayload = {
        jobTitleId: roleForm.jobTitles[0],
        moduleOperationId: roleForm.modules[0],
        canView: roleForm.canView,
        canEdit: roleForm.canEdit,
        canCreate: true,
        canDelete: true
      };
      handleUpdateRole(roleUpdatePayload, roleForm.jobTitleToModuleOperationId);
      return;
    }
    handleCreateRole({jobTitles, modules, canView: roleForm.canView, canEdit: roleForm.canEdit});
  };

  const isSubmitDisabled =
    selectedJobTitleIds.length === 0 || selectedModuleIds.length === 0;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 550,
        mx: 'auto',
        mt: 2.5,
        p: 2.5,
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        background:
          'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        color: 'white',
      }}
    >
      <Stack component="form" spacing={2.5} onSubmit={handleSubmit}>
        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="role-job-title-label">Job Titles</InputLabel>
          <Select
            labelId="role-job-title-label"
            multiple
            value={selectedJobTitleIds}
            onChange={(event) => setSelectedJobTitleIds(event.target.value)}
            input={<OutlinedInput label="Job Titles" />}
            renderValue={(selected) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {selected.map((id) => {
                  const jt = jobTitleOptions.find((j) => j.jobTitleId === id);
                  return (
                    <Chip
                      key={id}
                      label={jt?.jobTitleName}
                      size="small"
                      sx={{ bgcolor: 'rgba(215, 212, 212, 0.2)' }}
                    />
                  );
                })}
              </Stack>
            )}
            MenuProps={selectMenuProps}
          >
            {jobTitleOptions.map((jt) => (
              <MenuItem key={jt.jobTitleId} value={jt.jobTitleId}>
                <Checkbox
                  checked={selectedJobTitleIds.includes(jt.jobTitleId)}
                />
                <ListItemText primary={jt.jobTitleName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="role-module-label">Modules</InputLabel>
          <Select
            labelId="role-module-label"
            multiple
            value={selectedModuleIds}
            onChange={(event) => setSelectedModuleIds(event.target.value)}
            input={<OutlinedInput label="Modules" />}
            renderValue={(selected) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {selected.map((id) => {
                  const m = moduleOptions.find(
                    (mod) => mod.moduleOperationId === id
                  );
                  return (
                    <Chip
                      key={id}
                      label={m?.moduleOperationName}
                      size="small"
                      sx={{ bgcolor: 'rgba(215, 212, 212, 0.2)' }}
                      variant="outlined"
                    />
                  );
                })}
              </Stack>
            )}
            MenuProps={selectMenuProps}
          >
            {moduleOptions.map((m) => (
              <MenuItem key={m.moduleOperationId} value={m.moduleOperationId}>
                <Checkbox
                  checked={selectedModuleIds.includes(m.moduleOperationId)}
                />
                <ListItemText primary={m.moduleOperationName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Permissions
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={roleForm.canView}
                onChange={handleViewChange}
                disabled={roleForm.canEdit}
              />
            }
            label="Can View"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={roleForm.canEdit}
                onChange={handleEditChange}
              />
            }
            label="Can Edit"
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitDisabled}
          sx={{
            width: '180px',
            mt: 1,
            alignSelf: 'center',
            py: 1.2,
            fontSize: 14,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            background: '#4f8a3d',
            boxShadow: '0 4px 14px 0 rgba(79, 138, 61, 0.4)',
            '&:hover': {
              background: '#3f6f31',
              boxShadow: '0 6px 20px 0 rgba(79, 138, 61, 0.5)',
            },
            '&.Mui-disabled': {
              background: 'rgba(255,255,255,0.16)',
              color: 'rgba(255,255,255,0.45)',
              boxShadow: 'none',
            },
          }}
        >
          {roleForm.isUpdate ? 'Update Role' : 'Create Role'}
        </Button>
      </Stack>
    </Paper>
  );
}

export default RoleForm;